import { Link } from "react-router-dom";
import { Rocket, Check, Send, Trophy, PartyPopper, Sparkles } from "lucide-react";

/**
 * Activation + Celebration banners shown above the dashboard metric grid.
 *
 * State machine (driven entirely by real sdr_events counts — no flags in DB):
 *
 *   Stage 0  → No emails sent AND no leads
 *              → GuidedActivationCard (4-step checklist + pulsing CTA)
 *
 *   Stage 1  → Leads present but no emails sent yet
 *              → GuidedActivationCard (step 1 checked, step 2 pulses)
 *
 *   Stage 2  → First email sent, no bookings yet
 *              → FirstOutreachBanner ("🎉 Your AI just started working!")
 *
 *   Stage 3  → First booking landed
 *              → FirstBookingBanner ("🔥 Your AI booked its first meeting!")
 *              Shown for the first 24h after meeting_booked_at
 *
 *   Stage 4+ → Multiple bookings
 *              → DailyImpactBanner ("Your AI generated 7 meetings today 🚀")
 */

function computeStage({ funnel, leadsCount }) {
  const f = funnel || {};
  const sentToday = Number(f.emails_sent_today || 0);
  const bookingsToday = Number(f.meetings_booked_today || 0);
  const trend = f.trend || [];
  const totalSent =
    sentToday + trend.reduce((a, d) => a + (d.emails_sent || 0), 0);
  const totalBookings =
    bookingsToday + trend.reduce((a, d) => a + (d.meetings_booked || 0), 0);

  if (totalSent === 0 && (leadsCount || 0) === 0) return "stage-0";
  if (totalSent === 0) return "stage-1";
  if (totalBookings === 0) return "stage-2";
  if (totalBookings === 1 && bookingsToday === 1) return "stage-3";
  if (bookingsToday >= 1) return "stage-4-today";
  return "stage-4-quiet";
}

function ChecklistStep({ done, label, active }) {
  return (
    <div
      className={`flex items-center gap-2 text-[12.5px] ${
        done ? "text-emerald-700 line-through opacity-80" : active ? "text-slate-900 font-semibold" : "text-slate-500"
      }`}
      data-testid={`activation-step-${label.toLowerCase().replace(/\s+/g, "-")}`}
      data-done={done ? "true" : "false"}
      data-active={active ? "true" : "false"}
    >
      <span
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
          done
            ? "bg-emerald-500 border-emerald-500"
            : active
            ? "border-indigo-500 bg-indigo-50"
            : "border-slate-300 bg-white"
        }`}
      >
        {done ? (
          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
        ) : active ? (
          <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
        ) : null}
      </span>
      {label}
    </div>
  );
}

function GuidedActivationCard({ leadsCount, funnel }) {
  const hasLeads = (leadsCount || 0) > 0;
  const sentToday = Number(funnel?.emails_sent_today || 0);
  const trendSent = (funnel?.trend || []).reduce(
    (a, d) => a + (d.emails_sent || 0), 0,
  );
  const anySent = sentToday + trendSent > 0;
  const anyReplies = Number(funnel?.link_clicks_today || 0) > 0;
  const anyBookings = Number(funnel?.meetings_booked_today || 0) > 0;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-6 sm:p-7"
      data-testid="activation-card"
      data-stage={hasLeads ? "stage-1" : "stage-0"}
    >
      {/* Decorative corner glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-indigo-400/20 blur-3xl"
      />
      <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-indigo-700">
            <Sparkles className="h-3 w-3" />
            Getting started
          </div>
          <h2
            className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
            data-testid="activation-title"
          >
            🚀 Start your AI Sales Engine
          </h2>
          <p className="mt-1.5 text-[13.5px] text-slate-600 max-w-xl leading-snug">
            You don't have any data yet. Send your first outreach and watch
            AI generate insights instantly — the funnel updates in seconds.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <ChecklistStep label="Add leads" done={hasLeads} active={!hasLeads} />
            <ChecklistStep
              label="Send first email"
              done={anySent}
              active={hasLeads && !anySent}
            />
            <ChecklistStep
              label="Get first click or reply"
              done={anyReplies}
              active={anySent && !anyReplies}
            />
            <ChecklistStep
              label="Book first meeting"
              done={anyBookings}
              active={anyReplies && !anyBookings}
            />
          </div>
        </div>

        <div className="shrink-0 flex sm:flex-col sm:items-end gap-2">
          {/* Pulsing CTA — soft glow every 3s */}
          <Link
            to="/leads"
            data-testid="activation-cta"
            className="relative inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[13.5px] font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/30 transition-transform hover:scale-105"
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-xl bg-indigo-500/40 animate-ping-soft"
              style={{ animationDuration: "3s" }}
            />
            <Send className="relative h-4 w-4" />
            <span className="relative">
              {hasLeads ? "Send First Email" : "Add Your First Lead"}
            </span>
          </Link>
          <div className="text-[10.5px] text-slate-500 font-mono hidden sm:block">
            &lt; 2 minutes
          </div>
        </div>
      </div>
    </section>
  );
}

function FirstOutreachBanner() {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-5 sm:p-6"
      data-testid="first-outreach-banner"
    >
      <div className="flex items-center gap-4">
        <span className="inline-flex h-11 w-11 rounded-full bg-emerald-500/15 border border-emerald-200 items-center justify-center">
          <Rocket className="h-5 w-5 text-emerald-600" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] sm:text-[16px] font-extrabold tracking-tight text-slate-900">
            🎉 Your AI just started working!
          </div>
          <div className="text-[12.5px] text-slate-600 mt-0.5">
            First outreach sent — tracking performance now. Watch this
            dashboard as clicks and replies come in.
          </div>
        </div>
      </div>
    </section>
  );
}

function FirstBookingBanner({ bookingsToday }) {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-5 sm:p-6"
      data-testid="first-booking-banner"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-amber-300/30 blur-3xl"
      />
      <div className="relative flex items-center gap-4">
        <span className="inline-flex h-11 w-11 rounded-full bg-amber-500/20 border border-amber-300 items-center justify-center">
          <Trophy className="h-5 w-5 text-amber-600" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] sm:text-[16px] font-extrabold tracking-tight text-slate-900">
            🔥 Your AI booked its first meeting!
          </div>
          <div className="text-[12.5px] text-slate-600 mt-0.5">
            {bookingsToday >= 1
              ? "A lead just self-booked via the AI funnel. This is the moment your pipeline turns autonomous."
              : "Your first meeting is on the calendar. Keep volume steady — more clicks mean more bookings."}
          </div>
        </div>
      </div>
    </section>
  );
}

function DailyImpactBanner({ bookingsToday }) {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-5 shadow-md"
      data-testid="daily-impact-banner"
      data-bookings-today={bookingsToday}
    >
      <div className="relative flex items-center gap-3">
        <span className="inline-flex h-9 w-9 rounded-full bg-amber-500/20 border border-amber-400/30 items-center justify-center">
          <PartyPopper className="h-4 w-4 text-amber-400" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] sm:text-[15px] font-bold tracking-tight text-white">
            Your AI generated{" "}
            <span className="text-amber-300">
              {bookingsToday} {bookingsToday === 1 ? "meeting" : "meetings"}
            </span>{" "}
            today 🚀
          </div>
          <div className="text-[11.5px] text-slate-300 mt-0.5">
            Pipeline is compounding. Lock the winning subject line — click
            "Generate Variations" on your #1 message.
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ActivationRow({ funnel, leadsCount }) {
  const stage = computeStage({ funnel, leadsCount });
  const bookingsToday = Number(funnel?.meetings_booked_today || 0);

  if (stage === "stage-0" || stage === "stage-1") {
    return <GuidedActivationCard leadsCount={leadsCount} funnel={funnel} />;
  }
  if (stage === "stage-2") return <FirstOutreachBanner />;
  if (stage === "stage-3") return <FirstBookingBanner bookingsToday={bookingsToday} />;
  if (stage === "stage-4-today") return <DailyImpactBanner bookingsToday={bookingsToday} />;
  // stage-4-quiet: no banner — dashboard feels clean on low-activity days
  return null;
}
